import { errorMessage } from '../lib/api';
import { useMyAvailability } from '../lib/distribution';
import { formatDate } from '../lib/format';

/**
 * Liga/desliga "Disponível para receber leads". Ligado, a automação de
 * distribuição inclui a pessoa no rodízio; desligado (ou ausente pelo gestor),
 * pula para quem estiver ligado.
 *
 * `compact`: só a bolinha (menu lateral recolhido).
 * `tone="light"`: para fundo claro (tela do celular).
 */
export function AvailabilitySwitch({
  compact = false,
  tone = 'sidebar',
}: {
  compact?: boolean;
  tone?: 'sidebar' | 'light';
}) {
  const { me, toggle } = useMyAvailability();
  if (!me) return null;
  const absent = Boolean(me.absentUntil);
  const on = me.available;
  const label = absent
    ? `Ausente até ${formatDate(me.absentUntil)}${me.absentReason ? ` (${me.absentReason})` : ''}`
    : on
      ? 'Disponível para receber leads'
      : 'Indisponível — não recebe leads';
  const flip = () => !absent && !toggle.isPending && toggle.mutate(!on);
  const error = toggle.error ? errorMessage(toggle.error, 'Não foi possível mudar') : null;

  if (compact) {
    return (
      <button
        type="button"
        onClick={flip}
        disabled={absent}
        title={label}
        aria-label={label}
        className="flex h-6 w-6 items-center justify-center rounded-full border border-white/20"
      >
        <span
          className={`h-2.5 w-2.5 rounded-full ${on ? 'bg-emerald-400' : 'bg-slate-400'}`}
        />
      </button>
    );
  }

  const muted = tone === 'light' ? 'text-slate-500' : 'opacity-70';
  return (
    <div className="text-xs">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={flip}
        disabled={absent || toggle.isPending}
        className="flex w-full items-center gap-2 disabled:cursor-not-allowed"
        title={
          absent
            ? label
            : on
              ? 'Clique para parar de receber leads'
              : 'Clique para receber leads'
        }
      >
        <span
          className={`relative h-5 w-9 shrink-0 rounded-full transition ${
            on ? 'bg-emerald-500' : tone === 'light' ? 'bg-slate-300' : 'bg-white/25'
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
              on ? 'left-[18px]' : 'left-0.5'
            }`}
          />
        </span>
        <span className="min-w-0 text-left font-medium leading-tight">
          {absent ? 'Ausente' : on ? 'Disponível' : 'Indisponível'}
        </span>
      </button>
      <p className={`mt-1 leading-snug ${muted}`}>
        {absent
          ? label
          : on
            ? me.pending
              ? `${me.pending} lead(s) esperando seu contato`
              : 'Recebendo leads'
            : 'Ligue para receber leads'}
      </p>
      {error && <p className="mt-1 text-red-400">{error}</p>}
    </div>
  );
}
